'use client';

import BaseModal from '@c/modals/BaseModal';
import { prefix } from '@util/imagePrefix';
import { getBikeFitment } from 'app/product/utils/getBikeFitment';
import { PencilIcon, Trash2, Search } from 'lucide-react';
import { ProductDocument } from 'models/product';
import {
  UserBike,
  rideTypes,
  makeSchema,
  Make,
  MAKE_ARRAY,
} from 'models/shared';
import { UserDocument } from 'models/user';
import Image from 'next/image';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import FormLabel from '@c/forms/controls/FormLabel';
import FormSelect from '@c/forms/controls/FormSelect';
import Button from '@ui/Button';
import {
  addUserBike,
  editUserBike,
  removeUserBike,
} from '@util/firestore/users';
import { useToastContext } from 'context/ToastContext';
import useBikes from '@util/hooks/useBikes';
import { useAuth } from 'context/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import SafeImage from '@ui/SafeImage';

const createUniqueBikesSet = (bikes: UserBike[]): UserBike[] => {
  const uniqueBikes = new Map<string, UserBike>();

  bikes.forEach((bike) => {
    const key = `${bike.make}-${bike.model}-${bike.year}`;
    if (!uniqueBikes.has(key)) {
      uniqueBikes.set(key, bike);
    }
  });

  return Array.from(uniqueBikes.values()).sort((a, b) => {
    // First sort by year (newest first)
    const yearA = a.year ?? 0;
    const yearB = b.year ?? 0;
    if (yearB !== yearA) {
      return yearB - yearA;
    }
    // Then sort by make
    if (a.make !== b.make) {
      return a.make.localeCompare(b.make);
    }
    // Finally sort by model
    return (a.model || '').localeCompare(b.model || '');
  });
};

const formSchema = z.object({
  make: makeSchema,
  model: z.string().min(1, 'Model is required'),
  year: z
    .number()
    .min(1900, 'Year must be at least 1900')
    .max(new Date().getFullYear() + 1, 'Year cannot be in the future'),
  type: rideTypes,
});

type Form = z.infer<typeof formSchema>;

type EditYourGarageProps = {
  userDoc: UserDocument;
  product: ProductDocument;
};

export default function EditYourGarage({
  userDoc,
  product,
}: EditYourGarageProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('list');
  const [bikeToEdit, setBikeToEdit] = useState<UserBike | null>(null);
  const [bikeIndex, setBikeIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showSuccessToast } = useToastContext();

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
  } = useForm<Form>({
    resolver: zodResolver(formSchema),
    defaultValues: bikeToEdit
      ? {
          make: bikeToEdit.make as Make,
          model: bikeToEdit.model ?? '',
          year: bikeToEdit.year ?? new Date().getFullYear(),
          type: bikeToEdit.type,
        }
      : {
          make: '' as Make,
          model: '',
          year: new Date().getFullYear(),
          type: 'Dirt Bikes',
        },
  });

  const selectedMake = watch('make');
  const selectedType = watch('type');
  const selectedYear = watch('year');
  const { bikeModelsByMake, bikeYears } = useBikes(
    [selectedYear.toString()],
    [selectedMake],
    [selectedType]
  );

  const onSubmit = async (data: Form) => {
    try {
      setIsSubmitting(true);
      const updatedBike: UserBike = {
        make: data.make,
        model: data.model,
        year: data.year,
        type: data.type,
        date_added: Date.now(),
        downloadUrl: '',
        imageUrl: '',
        in_feed: false,
      };

      if (bikeToEdit && bikeIndex !== null) {
        await editUserBike(userDoc, updatedBike, bikeIndex);
        showSuccessToast('Bike updated successfully');
      } else {
        await addUserBike(userDoc.uid, updatedBike);
        showSuccessToast('Bike added successfully');
      }

      setModalMode('list');
      setBikeToEdit(null);
      setBikeIndex(null);
    } catch (error) {
      console.error('Error updating bike:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!userDoc) return;

  return (
    <div className="flex items-center gap-x-2">
      <div className="flex flex-col gap-y-4">
        <div className="flex items-center gap-x-2">
          <span className="text-[1.8rem] font-semibold text-black">
            Your Garage
          </span>
          <button
            type="button"
            aria-label="Edit garage"
            onClick={() => setModalOpen(true)}
          >
            <PencilIcon className="h-8 w-8 text-black" />
          </button>
        </div>
      </div>

      <BaseModal
        isOpen={modalOpen}
        dismiss={() => {
          setModalOpen(false);
          setModalMode('list');
          setBikeToEdit(null);
          setBikeIndex(null);
        }}
        title="Edit Your Garage"
        overflowType="visible"
      >
        {modalMode === 'list' ? (
          <div className="relative flex h-[50rem] w-[50rem] flex-col gap-y-6 overflow-hidden rounded-b-[3rem] p-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Search your garage"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-[1.5rem] border border-zinc-200 px-4 py-2 pl-14 text-[1.8rem]"
              />
              <Search className="absolute left-4 top-1/2 h-7 w-7 -translate-y-1/2 text-zinc-400" />
            </div>

            <div className="flex flex-col gap-y-8 overflow-y-auto pb-32">
              {createUniqueBikesSet(userDoc?.bikes || []).map((bike, index) => {
                const parsedBrandName = bike.make
                  .split(' ')
                  .join('-')
                  .toLowerCase();
                const brandImage = `${prefix}/assets/brands/${parsedBrandName}_1920x2560.png`;

                const fitment = getBikeFitment(
                  product?.fitting_bikes?.bikes ?? [],
                  [bike],
                  !!product.integration_info
                );

                return (
                  <div
                    key={`${bike.make}-${bike.model ?? ''}-${bike.year ?? ''}`}
                    className="flex h-[7rem] w-full items-center justify-between rounded-[2rem] border border-zinc-200 p-4"
                  >
                    <div className="flex items-center gap-x-6">
                      <SafeImage
                        alt={bike.make}
                        src={brandImage}
                        height={200}
                        width={200}
                        fallback={
                          <div className="flex h-[7rem] w-[7rem] items-center justify-center rounded-[0.5rem] bg-zinc-200 p-1 text-center text-[1rem] font-medium text-zinc-600">
                            {bike.make}
                          </div>
                        }
                        className="h-[6rem] w-[6rem] object-contain"
                      />

                      <div className="flex flex-col gap-y-0">
                        <span className="text-[1.4rem] font-medium text-black">
                          {bike.year} {bike.make} {bike.model}
                        </span>

                        <span
                          className={`text-[1.2rem] font-medium ${
                            fitment?.status === 'fits'
                              ? 'text-[#1E7A1E]'
                              : fitment?.status === 'may-fit'
                              ? 'text-[#FFB800]'
                              : 'text-[#7F7F7F]'
                          }`}
                        >
                          {fitment?.status === 'fits'
                            ? 'Fits your vehicle'
                            : fitment?.status === 'may-fit'
                            ? 'May fit your vehicle'
                            : "Doesn't fit"}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-x-4">
                      <button
                        type="button"
                        aria-label="Edit bike"
                        className="flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100 transition-colors hover:bg-zinc-200"
                        onClick={() => {
                          setModalMode('edit');
                          setBikeToEdit(bike);
                          setBikeIndex(index);
                          reset({
                            make: bike.make as Make,
                            model: bike.model ?? '',
                            year: bike.year ?? new Date().getFullYear(),
                            type: bike.type,
                          });
                        }}
                      >
                        <PencilIcon className="h-6 w-6 text-zinc-600" />
                      </button>
                      <button
                        type="button"
                        aria-label="Remove bike"
                        className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 transition-colors hover:bg-red-100"
                        onClick={async () => {
                          await removeUserBike(userDoc.uid, bike);
                          showSuccessToast('Bike removed successfully');
                        }}
                      >
                        <Trash2 className="h-6 w-6 text-red-500" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="absolute bottom-0 left-0 right-0 rounded-b-[3rem] border-t border-zinc-200 bg-white pb-6 pt-6">
              <div className="px-6">
                <Button
                  type="primary"
                  text="Add New Bike"
                  className="w-full bg-brand-secondary py-6 text-[1.8rem] font-semibold text-white"
                  onClick={() => {
                    setModalMode('edit');
                    setBikeToEdit(null);
                    setBikeIndex(null);
                    reset({
                      make: '' as Make,
                      model: '',
                      year: new Date().getFullYear(),
                      type: 'Dirt Bikes',
                    });
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-y-8 rounded-b-[3rem] p-6">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-y-6"
            >
              <FormLabel value="Type" required>
                <FormSelect
                  control={control}
                  name="type"
                  options={Object.values(rideTypes.options).map((type) => ({
                    label: type.value,
                    value: type.value,
                    id: type.value,
                  }))}
                  error={!!errors.type}
                  disabled={isSubmitting}
                />
                {errors.type?.message && (
                  <span className="text-xs text-red-500">
                    {errors.type.message}
                  </span>
                )}
              </FormLabel>

              <FormLabel value="Year" required>
                <FormSelect
                  control={control}
                  name="year"
                  options={bikeYears.map((year) => ({
                    label: year.toString(),
                    value: year,
                    id: year.toString(),
                  }))}
                  error={!!errors.year}
                  disabled={isSubmitting}
                />
                {errors.year?.message && (
                  <span className="text-xs text-red-500">
                    {errors.year.message}
                  </span>
                )}
              </FormLabel>

              <FormLabel value="Make" required>
                <FormSelect
                  control={control}
                  name="make"
                  options={MAKE_ARRAY.map((make) => ({
                    label: make,
                    value: make,
                    id: make,
                  }))}
                  error={!!errors.make}
                  disabled={isSubmitting}
                />
                {errors.make?.message && (
                  <span className="text-xs text-red-500">
                    {errors.make.message}
                  </span>
                )}
              </FormLabel>

              <FormLabel value="Model" required>
                <FormSelect
                  control={control}
                  name="model"
                  options={
                    selectedMake && bikeModelsByMake[selectedMake]
                      ? bikeModelsByMake[selectedMake].map((bike) => ({
                          label: bike.model,
                          value: bike.model,
                          id: bike.model,
                        }))
                      : []
                  }
                  error={!!errors.model}
                  disabled={isSubmitting}
                />
                {errors.model?.message && (
                  <span className="text-xs text-red-500">
                    {errors.model.message}
                  </span>
                )}
              </FormLabel>

              <div className="flex justify-end gap-x-4">
                <Button
                  type="secondary"
                  text="Cancel"
                  onClick={() => {
                    setModalMode('list');
                    setBikeToEdit(null);
                    setBikeIndex(null);
                  }}
                  disabled={isSubmitting}
                />
                <Button
                  type="primary"
                  text={isSubmitting ? 'Saving...' : 'Save Changes'}
                  buttonType="submit"
                  disabled={isSubmitting}
                />
              </div>
            </form>
          </div>
        )}
      </BaseModal>
    </div>
  );
}
